import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AppearanceSection } from "@/components/appearance-section";
import { AccountSection, type AccountSectionUser } from "./account-section";
import { DesktopProfileLayout } from "./desktop-profile-layout";
import { MobileProfileLayout } from "./mobile-profile-layout";
import { NotificationsSection } from "./notifications-section";
import type { ProfileSection } from "@/hooks/use-profile-url";
export type { ProfileSection };

export interface ProfileNavLink {
  icon?: React.ReactNode;
  label: string;
  value?: React.ReactNode;
  onClick: () => void;
}

export type ProfileGroup =
  | {
      kind: "sections";
      label: string;
      items: { id: ProfileSection; label: string }[];
    }
  | {
      kind: "links";
      label: string;
      items: ProfileNavLink[];
    };

export interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeSection: ProfileSection;
  onSectionChange: (section: ProfileSection) => void;
  user: AccountSectionUser;
  onSignOut: () => void;
  beheerLinks?: ProfileNavLink[];
  systeemLinks?: ProfileNavLink[];
}

export function ProfileDialog({
  open,
  onOpenChange,
  activeSection,
  onSectionChange,
  user,
  onSignOut,
  beheerLinks,
  systeemLinks,
}: ProfileDialogProps) {
  const { t } = useTranslation();
  const [mobileSection, setMobileSection] = useState<ProfileSection | null>(null);

  useEffect(() => {
    if (open) setMobileSection(null);
  }, [open]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) setMobileSection(null);
    onOpenChange(nextOpen);
  }

  const sectionLabels: Record<ProfileSection, string> = {
    account: t("profile-dialog.sections.account.label"),
    appearance: t("profile-dialog.sections.appearance.label"),
    notifications: t("profile-dialog.sections.notifications.label"),
  };

  const groups: ProfileGroup[] = [
    {
      kind: "sections",
      label: t("profile-dialog.groups.account.label"),
      items: [{ id: "account", label: sectionLabels.account }],
    },
    {
      kind: "sections",
      label: t("profile-dialog.groups.preferences.label"),
      items: [
        { id: "appearance", label: sectionLabels.appearance },
        { id: "notifications", label: sectionLabels.notifications },
      ],
    },
  ];
  if (beheerLinks?.length) {
    groups.push({
      kind: "links",
      label: t("profile-dialog.groups.admin.label"),
      items: beheerLinks,
    });
  }
  if (systeemLinks?.length) {
    groups.push({
      kind: "links",
      label: t("profile-dialog.groups.system.label"),
      items: systeemLinks,
    });
  }

  const sectionContent = (section: ProfileSection) => {
    switch (section) {
      case "account":
        return <AccountSection user={user} />;
      case "appearance":
        return <AppearanceSection />;
      case "notifications":
        return <NotificationsSection />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="gap-0 overflow-hidden p-0 max-md:!top-0 max-md:!left-0 max-md:h-[100dvh] max-md:max-h-[100dvh] max-md:w-full max-md:!max-w-full max-md:!translate-x-0 max-md:!translate-y-0 max-md:!grid-rows-[1fr] max-md:rounded-none md:aspect-[16/10] md:max-h-[80vh] md:w-full md:max-w-3xl md:ring-0"
        showCloseButton={false}
        aria-describedby={undefined}
      >
        <DesktopProfileLayout
          groups={groups}
          activeSection={activeSection}
          activeSectionLabel={sectionLabels[activeSection]}
          onSectionChange={onSectionChange}
          onClose={() => handleOpenChange(false)}
          onSignOut={onSignOut}
        >
          {sectionContent(activeSection)}
        </DesktopProfileLayout>

        <MobileProfileLayout
          groups={groups}
          mobileSection={mobileSection}
          mobileSectionLabel={mobileSection ? sectionLabels[mobileSection] : null}
          onSectionSelect={(section) => {
            setMobileSection(section);
            onSectionChange(section);
          }}
          onBack={() => setMobileSection(null)}
          onClose={() => handleOpenChange(false)}
          onSignOut={onSignOut}
        >
          {mobileSection !== null ? sectionContent(mobileSection) : null}
        </MobileProfileLayout>
      </DialogContent>
    </Dialog>
  );
}
